import { Component, Inject } from '@angular/core';
import {
    AbstractControl,
    UntypedFormArray,
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
    Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { IDynamicKeyValue, INameId } from '@modeso/types__aumico-lib-core-be';
import { ISuperTableOptions, SuperTableColumnType } from '@modeso/types__aumico-lib-notes-be';
import { IInputDataType } from '@modeso/types__aumico-lib-notes-be/dist/models/enums/input-type.enum';
import { SuperTableColumnBelongingYear } from '@modeso/types__aumico-lib-notes-be/dist/models/enums/super_table_column_belonging_year.enum';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextService } from 'angular-i18next';
import { cloneDeep } from 'lodash-es';
import { distinctUntilChanged } from 'rxjs';
import { EMPTY_ARRAY_LENGTH } from '../../../../../../aumico-lib-core-fe/src/lib';
import { ISuperTableOptionsDialogData } from '../../../interfaces/super-table-options-dialog-data.interface';
import {
    ISuperTableOptionsFormValue,
    ISuperTableSegmentFormValue,
    IXAxisConfigColumnFormValue,
} from '../../../interfaces/super-table-options-form-value.interface';
import { NotesOptionsBase } from '../../NotesOptionsBase';

@UntilDestroy()
@Component({
    selector: 'aumico-notes-super-table-options',
    templateUrl: './super-table-options.component.html',
    styleUrls: ['./super-table-options.component.scss'],
})
export class SuperTableOptionsFormDialogComponent extends NotesOptionsBase {
    form: UntypedFormGroup | undefined;
    xAxisConfigColTypesSelections: INameId[] = [];
    xAxisConfigColBelongingYearSelections: INameId[] = [];
    columnOptions: INameId[] = [];
    dataType = IInputDataType;

    constructor(
        private readonly dialogRef: MatDialogRef<SuperTableOptionsFormDialogComponent, ISuperTableOptions | undefined>,
        private matIconRegistry: MatIconRegistry,
        private domSanitizer: DomSanitizer,
        private localizedTexts: I18NextService,
        private formBuilder: UntypedFormBuilder,
        @Inject(MAT_DIALOG_DATA) public data: ISuperTableOptionsDialogData,
    ) {
        super();
        this.addSvgIcon('interface-delete 1');
        this.addSvgIcon('aumico-pattern-icon-remove');
        this.preparexAxisConfigColTypesSelections();
        this.prepareXAxisConfigColBelongingYearSelections();

        if (data.options == null) {
            this.initializeEmptyForm();
        } else {
            this.initializeFormWithSavedOptions(data.options);
        }
    }

    ngOnInit(): void {}

    private initializeEmptyForm(): void {
        this.form = this.formBuilder.group({
            title: new UntypedFormControl(undefined, Validators.required),
            xAxisConfig: this.formBuilder.array([], Validators.required),
            segments: this.formBuilder.array([]),
            compareYears: new UntypedFormControl(true, Validators.required),
            transpose: new UntypedFormControl(false),
        });

        const xAxisConfig = this.getXAxisConfigColsAsFormArray();
        this.form.setControl('xAxisConfig', xAxisConfig);

        const segments = this.getSegmentsAsFormArray();
        this.form.setControl('segments', segments);

        this.addCompareYearsColumnOptions();
        this.listenToFormChanges();
    }

    private initializeFormWithSavedOptions(options: ISuperTableOptions) {
        const mappedSuperTableOptions = this.mapSuperTableOptionsToFormValue(options);

        this.form = this.formBuilder.group({
            title: new UntypedFormControl(mappedSuperTableOptions.title, Validators.required),
            xAxisConfig: this.formBuilder.array([], Validators.required),
            segments: this.formBuilder.array([], Validators.required),
            compareYears: new UntypedFormControl(mappedSuperTableOptions.compareYears, Validators.required),
            transpose: new UntypedFormControl(mappedSuperTableOptions.transpose),
        });

        const xAxisConfigFormArray = this.form.get('xAxisConfig') as UntypedFormArray;

        for (const col of mappedSuperTableOptions.xAxisConfig) {
            if (
                col.columnId === SuperTableColumnType.CURRENT_YEAR ||
                col.columnId === SuperTableColumnType.PREVIOUS_YEAR
            ) {
                continue;
            }
            xAxisConfigFormArray.push(
                this.formBuilder.group({
                    columnId: new UntypedFormControl(col.columnId, Validators.required),
                    type: new UntypedFormControl(col.type, Validators.required),
                    title: new UntypedFormControl(col.title),
                    belongingYear: new UntypedFormControl(col.belongingYear, Validators.required),
                }),
            );

            this.columnOptions.push({
                id: col.columnId,
                name: col.title,
            });
        }

        const segmentsFormArray = this.form.get('segments') as UntypedFormArray;

        mappedSuperTableOptions.segments.forEach((seg) => {
            segmentsFormArray.push(
                this.formBuilder.group({
                    title: new UntypedFormControl(seg.title, Validators.required),
                    childColsIds: new UntypedFormControl(seg.childColsIds, Validators.required),
                }),
            );
        });

        if (mappedSuperTableOptions.compareYears) {
            this.addCompareYearsColumnOptions();
        }

        this.listenToFormChanges();
    }

    private getXAxisConfigColsAsFormArray(): UntypedFormArray {
        const formGroups: AbstractControl[] = [this.addXAxisConfigColsFormGroup()];
        return new UntypedFormArray(formGroups);
    }

    private getSegmentsAsFormArray(): UntypedFormArray {
        const formGroups: AbstractControl[] = [this.addSegmentsFormGroup()];
        return new UntypedFormArray(formGroups);
    }

    private addXAxisConfigColsFormGroup(): UntypedFormGroup {
        return this.formBuilder.group({
            columnId: new UntypedFormControl('col' + this.form.get('xAxisConfig').value.length, Validators.required),
            type: new UntypedFormControl(undefined, Validators.required),
            title: new UntypedFormControl(undefined),
            belongingYear: new UntypedFormControl(undefined, Validators.required),
        });
    }

    private addSegmentsFormGroup(): UntypedFormGroup {
        return this.formBuilder.group({
            title: new UntypedFormControl(undefined, Validators.required),
            childColsIds: new UntypedFormControl([], Validators.required),
        });
    }

    addXAxisConfigCol(): void {
        const xAxisConfigColFormArray = this.form.get('xAxisConfig') as UntypedFormArray;

        const newXAxisConfigColFormGroup = this.addXAxisConfigColsFormGroup();

        xAxisConfigColFormArray.push(newXAxisConfigColFormGroup);
    }

    addSegment(): void {
        const segmentsFormArray = this.form.get('segments') as UntypedFormArray;

        const newSegmentFormGroup = this.addSegmentsFormGroup();

        segmentsFormArray.push(newSegmentFormGroup);
    }

    removeXAxisConfigCol(index: number): void {
        const xAxisConfigColFormArray = this.form.get('xAxisConfig') as UntypedFormArray;

        const colToRemove: IXAxisConfigColumnFormValue = xAxisConfigColFormArray.at(index).value;

        this.columnOptions = this.columnOptions.filter((col) => col.id !== colToRemove.columnId);

        xAxisConfigColFormArray.removeAt(index);

        // remove from segment child col selections if selected
        const segmentsFormArray = this.form.get('segments');
        const segments: ISuperTableSegmentFormValue[] = segmentsFormArray.value;

        if (segments.length !== EMPTY_ARRAY_LENGTH) {
            segments.forEach((segment) => {
                segment.childColsIds = segment.childColsIds?.filter((col) => col.id !== colToRemove.columnId);
            });

            this.form.get('segments').setValue(segments);
        }
    }

    removeSegment(index: number): void {
        const segmentsFormArray = this.form.get('segments') as UntypedFormArray;
        segmentsFormArray.removeAt(index);
    }

    private listenToFormChanges(): void {
        this.form
            .get('xAxisConfig')
            .valueChanges.pipe(untilDestroyed(this))
            .subscribe((xAxisConfigCols: IXAxisConfigColumnFormValue[]) => {
                const existingColIds = this.columnOptions.map((col) => col.id);
                for (const xAxisConfigCol of xAxisConfigCols) {
                    if (xAxisConfigCol.title !== null && !existingColIds.includes(xAxisConfigCol.columnId)) {
                        this.columnOptions = [
                            ...this.columnOptions,
                            {
                                id: xAxisConfigCol.columnId,
                                name: xAxisConfigCol.title,
                            },
                        ];
                    } else if (xAxisConfigCol.title !== null && existingColIds.includes(xAxisConfigCol.columnId)) {
                        const matchingOption = this.columnOptions.find((col) => col.id === xAxisConfigCol.columnId);

                        if (matchingOption.name === xAxisConfigCol.title) {
                            continue;
                        }
                        const matchingOptionIndex = this.columnOptions.findIndex(
                            (col) => col.id === xAxisConfigCol.columnId,
                        );

                        this.columnOptions.splice(matchingOptionIndex, 1);
                        this.columnOptions = [
                            ...this.columnOptions,
                            {
                                id: xAxisConfigCol.columnId,
                                name: xAxisConfigCol.title,
                            },
                        ];
                    }
                }
            });

        this.form
            .get('compareYears')
            .valueChanges.pipe(distinctUntilChanged(), untilDestroyed(this))
            .subscribe((compareYears: boolean) => {
                if (compareYears) {
                    this.addCompareYearsColumnOptions();
                } else {
                    this.columnOptions = this.columnOptions.filter(
                        (col) => col.id !== SuperTableColumnType.PREVIOUS_YEAR,
                    );

                    // remove from segment childCols 'previous year col' if selected
                    const segmentsFormArray = this.form.get('segments');
                    const segments: ISuperTableSegmentFormValue[] = segmentsFormArray.value;

                    segments.forEach((segment) => {
                        segment.childColsIds = segment.childColsIds.filter(
                            (col) => col.id !== SuperTableColumnType.PREVIOUS_YEAR,
                        );
                    });

                    this.form.get('segments').setValue(segments);
                }
            });
    }

    private addCompareYearsColumnOptions(): void {
        const existingColIds = this.columnOptions.map((col) => col.id);
        if (!existingColIds.includes(SuperTableColumnType.CURRENT_YEAR)) {
            this.columnOptions = [
                ...this.columnOptions,
                {
                    id: SuperTableColumnType.CURRENT_YEAR,
                    name: this.localizedTexts.t('aumico_notes_super_table_options_col_type_current_year') as string,
                },
            ];
        }
        if (!existingColIds.includes(SuperTableColumnType.PREVIOUS_YEAR)) {
            this.columnOptions = [
                ...this.columnOptions,
                {
                    id: SuperTableColumnType.PREVIOUS_YEAR,
                    name: this.localizedTexts.t('aumico_notes_super_table_options_col_type_previous_year') as string,
                },
            ];
        }
    }

    onSaveButtonClick(): void {
        const superTableOptionsMappedValue = this.mapSuperTableOptionsFormValueToOutputOptions(this.form.value);
        this.dialogRef.close(superTableOptionsMappedValue);
    }

    closeDialog(): void {
        this.dialogRef.close(undefined);
    }

    private preparexAxisConfigColTypesSelections(): void {
        this.xAxisConfigColTypesSelections.push(
            {
                id: SuperTableColumnType.TEXT,
                name: this.localizedTexts.t('aumico_notes_super_table_options_col_type_text') as string,
            },
            {
                id: SuperTableColumnType.NUMBER,
                name: this.localizedTexts.t('aumico_notes_super_table_options_col_type_number') as string,
            },
        );
    }

    private prepareXAxisConfigColBelongingYearSelections(): void {
        this.xAxisConfigColBelongingYearSelections.push(
            {
                id: SuperTableColumnBelongingYear.CURRENT_YEAR,
                name: this.localizedTexts.t('aumico_notes_super_table_options_col_belonging_year_current') as string,
            },
            {
                id: SuperTableColumnBelongingYear.PREVIOUS_YEAR,
                name: this.localizedTexts.t('aumico_notes_super_table_options_col_belonging_year_previous') as string,
            },
            {
                id: SuperTableColumnBelongingYear.NO_YEAR,
                name: this.localizedTexts.t('aumico_notes_super_table_options_col_belonging_year_none') as string,
            },
        );
    }

    private mapSuperTableOptionsFormValueToOutputOptions(formValue: ISuperTableOptionsFormValue): ISuperTableOptions {
        const modifiedFormValue: ISuperTableOptionsFormValue = cloneDeep<ISuperTableOptionsFormValue>(formValue);

        if (modifiedFormValue.compareYears) {
            // add column for previous year
            modifiedFormValue.xAxisConfig.unshift({
                columnId: SuperTableColumnType.PREVIOUS_YEAR,
                type: { id: SuperTableColumnType.PREVIOUS_YEAR, name: SuperTableColumnType.PREVIOUS_YEAR },
                belongingYear: {
                    id: SuperTableColumnBelongingYear.PREVIOUS_YEAR,
                    name: SuperTableColumnBelongingYear.PREVIOUS_YEAR,
                },
            });
        }

        // add default column for current year
        modifiedFormValue.xAxisConfig.unshift({
            columnId: SuperTableColumnType.CURRENT_YEAR,
            type: { id: SuperTableColumnType.CURRENT_YEAR, name: SuperTableColumnType.CURRENT_YEAR },
            belongingYear: {
                id: SuperTableColumnBelongingYear.CURRENT_YEAR,
                name: SuperTableColumnBelongingYear.CURRENT_YEAR,
            },
        });

        return {
            title: this.getTitleWithLanguageKeys(modifiedFormValue.title),
            xAxisConfig: modifiedFormValue.xAxisConfig.map((col) => {
                if (col.type.id === SuperTableColumnType.NUMBER || col.type.id === SuperTableColumnType.TEXT) {
                    return {
                        columnId: col.columnId,
                        title: this.getTitleWithLanguageKeys(col.title),
                        type: col.type.id as SuperTableColumnType,
                        belongingYear: col.belongingYear.id as SuperTableColumnBelongingYear,
                    };
                }

                return {
                    columnId: col.columnId,
                    type: col.type.id as SuperTableColumnType,
                    belongingYear: col.belongingYear.id as SuperTableColumnBelongingYear,
                };
            }),
            segments: modifiedFormValue.segments.map((col, index: number) => {
                return {
                    segmentId: 'segment' + index,
                    title: col.title ? this.getTitleWithLanguageKeys(col.title) : undefined,
                    childColIds: col.childColsIds.map((childColsIds) => childColsIds.id),
                };
            }),
            compareYears: modifiedFormValue.compareYears,
            hideCurrentYear: modifiedFormValue.hideCurrentYear,
            showFiscalYear: modifiedFormValue.showFiscalYear,
            transpose: modifiedFormValue.transpose,
            showPreviousYear: false,
            showCurrency: modifiedFormValue.showCurrency,
        };
    }

    private mapSuperTableOptionsToFormValue(superTableOptions: ISuperTableOptions): ISuperTableOptionsFormValue {
        return {
            title: superTableOptions.title['de-ch'],
            xAxisConfig: superTableOptions.xAxisConfig.map((col) => {
                return {
                    columnId: col.columnId,
                    title: col.title ? col.title['de-ch'] : undefined,
                    type: {
                        id: col.type,
                        name: this.getColumnTypeName(col.type),
                    },
                    belongingYear: {
                        id: col.belongingYear,
                        name: this.getColumnBelongingYearName(col.belongingYear),
                    },
                };
            }),
            segments: superTableOptions.segments.map((seg) => {
                return {
                    segmentId: seg.segmentId,
                    title: seg.title['de-ch'],
                    childColsIds: seg.childColIds.map((colId) => {
                        if (colId === SuperTableColumnType.CURRENT_YEAR) {
                            return {
                                id: SuperTableColumnType.CURRENT_YEAR,
                                name: this.localizedTexts.t(
                                    'aumico_notes_super_table_options_col_type_current_year',
                                ) as string,
                            };
                        }
                        if (colId === SuperTableColumnType.PREVIOUS_YEAR) {
                            return {
                                id: SuperTableColumnType.PREVIOUS_YEAR,
                                name: this.localizedTexts.t(
                                    'aumico_notes_super_table_options_col_type_previous_year',
                                ) as string,
                            };
                        }
                        return {
                            id: colId,
                            name: superTableOptions.xAxisConfig.find((col) => col.columnId === colId).title['de-ch'],
                        };
                    }),
                };
            }),
            compareYears: superTableOptions.compareYears,
            showFiscalYear: superTableOptions.showFiscalYear,
            hideCurrentYear: superTableOptions.hideCurrentYear,
            transpose: superTableOptions.transpose,
            showCurrency: superTableOptions.showCurrency ?? false,
        } as ISuperTableOptionsFormValue;
    }

    private getColumnTypeName(type: SuperTableColumnType): string {
        switch (type) {
            case SuperTableColumnType.CURRENT_YEAR: {
                return this.localizedTexts.t('aumico_notes_super_table_options_col_type_current_year') as string;
            }

            case SuperTableColumnType.PREVIOUS_YEAR: {
                return this.localizedTexts.t('aumico_notes_super_table_options_col_type_previous_year') as string;
            }

            case SuperTableColumnType.TEXT: {
                return this.localizedTexts.t('aumico_notes_super_table_options_col_type_text') as string;
            }

            case SuperTableColumnType.NUMBER: {
                return this.localizedTexts.t('aumico_notes_super_table_options_col_type_number') as string;
            }

            default:
                return this.localizedTexts.t('aumico_notes_super_table_options_col_type_text') as string;
        }
    }

    private getColumnBelongingYearName(belongingYear: SuperTableColumnBelongingYear): string {
        switch (belongingYear) {
            case SuperTableColumnBelongingYear.CURRENT_YEAR: {
                return this.localizedTexts.t('aumico_notes_super_table_options_col_belonging_year_current') as string;
            }

            case SuperTableColumnBelongingYear.PREVIOUS_YEAR: {
                return this.localizedTexts.t('aumico_notes_super_table_options_col_belonging_year_previous') as string;
            }

            case SuperTableColumnBelongingYear.NO_YEAR: {
                return this.localizedTexts.t('aumico_notes_super_table_options_col_belonging_year_none') as string;
            }
        }
    }

    private getTitleWithLanguageKeys(title: string): IDynamicKeyValue {
        const textWithLanguageKeys: IDynamicKeyValue = {};
        for (const language of this.data.languages) {
            textWithLanguageKeys[language] = title;
        }
        return textWithLanguageKeys;
    }

    private addSvgIcon(name: string): void {
        this.matIconRegistry.addSvgIcon(
            name,
            this.domSanitizer.bypassSecurityTrustResourceUrl(`/assets/icons/${name}.svg`),
        );
    }
}
